<template>
  <div class="wizzard__form">
    <UIList
      :get-key="item => item.key"
      :columns
      :items
      class="private-fund-values"
      @remove="handleRemove"
    >
      <template #account="{ item, hovered }">
        <UIAssetName
          :name="entityLabel(item.entity)"
          class="line-clamp-2"
        />
        <UIAddRowButton
          v-if="hovered"
          v-tooltip.top="`Add price for ${entityLabel(item.entity)}`"
          @click="handleAddRow(item.entity, item.index)"
        />
      </template>
      <template #date="{ item }">
        <UIInputDate
          v-model="item.price.date"
          placeholder="Date"
          :size
        />
      </template>
      <template #close="{ item }">
        <UIInputNumber
          v-model="item.price.close"
          :placeholder="`Value in ${currency}`"
          :size
          @ready="(e: HTMLInputElement) => e.focus()"
        />
      </template>
      <template #source="{ item }">
        <UIInputSelect
          v-model="item.price.source"
          v-tooltip="{
            content: SOURCE_TOOLTIP,
            html: true
          }"
          :data="ASSET_PRICE_SOURCE_EDITABLE"
          :size
          placeholder="Source"
        />
      </template>
    </UIList>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, inject, onMounted, onUnmounted } from 'vue'
import { uniqBy } from 'lodash'

import { AssetPriceForm, InputSize } from '@types'
import { WizardEntity } from '../utils/types'

import { 
  ASSET_PRICE_SOURCE_EDITABLE, 
  SOURCE_TOOLTIP 
} from '@/entities/assets/components/utils/const'

import { getCurrentDate } from '@/helpers/dates'
import { generateId } from '@/entities/utils/helpers'
import { getEntityAccountName } from '../utils/helpers'

import { useAssetsBunchStore } from '@/store/assets/bunch'

import { UIAssetName, UIList } from '@ui/core'
import { UIAddRowButton } from '@ui/buttons'
import { UIInputDate, UIInputNumber, UIInputSelect } from '@ui/fields'

type Item = {
  index: number,
  entity: WizardEntity, 
  price: AssetPriceForm
}

type Props = {
  entities: WizardEntity[]
}

const props = defineProps<Props>()

const assetsBunchStore = useAssetsBunchStore()

const size = inject<InputSize>('size')
const currency = inject<ComputedRef<string>>('currency')

const columns = computed(() => [
  {
    name: 'account',
    caption: 'Investor',
  },
  {
    name: 'date',
    caption: 'Date',
  },
  {
    name: 'close',
    caption: 'Close',
  },
  {
    name: 'source',
    caption: 'Source',
  },
])

const items = computed(() =>
  props.entities.reduce((acc, entity) => {
    acc.push(...entity.prices.map((price, index) => ({
      key: generateId(),
      index: index,
      entity,
      price,
    })))
    return acc
  }, [] as Item[]),
)

const addPrice = (entity: WizardEntity, index = 0) => {
  const price = {
    date: getCurrentDate({}),
    close: null,
    source: ASSET_PRICE_SOURCE_EDITABLE[0].value,
  }
  entity.prices.splice(index, 0, { ...price })
} 

const entityLabel = (entity: WizardEntity) => 
  getEntityAccountName(entity, id => assetsBunchStore.getElementById(id))

const handleAddRow = (entity: WizardEntity, index: number) => {
  addPrice(entity, index + 1)
}

const handleRemove = (_: number, item: Item) => {
  if (item.entity.prices.length === 1) {
    const price = item.entity.prices[0]
    price.date = getCurrentDate({})
    price.close = null
    price.source = ASSET_PRICE_SOURCE_EDITABLE[0].value
    return
  }
  item.entity.prices.splice(item.index, 1)
}

onMounted(() => {
  props.entities.forEach(entity => {
    if (entity.prices.length) return
    addPrice(entity)
  })
})

onUnmounted(() => {
  props.entities.forEach(entity => {
    entity.prices = entity.prices.filter(price => price.close !== null)
    entity.prices = uniqBy(entity.prices, 'date')
  })
})
</script>


<style>
.private-fund-values {
  @apply grid-cols-[minmax(120px,1fr)_140px_100px_110px_min-content];
  @apply sm:grid-cols-[1fr_120px_90px_90px_min-content];

  .ui-add-row-button {
    @apply top-full!;
  }

  .v-popper {
    @apply w-full;
  }
}
</style>
